/** @format */

import Compressor from 'compressorjs';
import { observable, action, computed } from 'mobx';
import database from '../firebase/config';
import { storage } from '../firebase/config';
import _ from 'lodash';
import {
  requestProgresses,
  requestStatus,
  productConditionsMapped,
} from '../constants';
import { getTimestamp } from '../helpers/getTimestamp';
import { getDateFromTimestamp } from '../helpers/getDateFromTimestamp';
import getTimestampFromDate from '../helpers/getTimestampFromDate';
import { newReqPart, newPartOption } from '../models/parts';
import { newRequest } from '../models/requests';
import { validateForm, formModels } from '../helpers/validateForm';
import CarPartsStore from './CarPartsStore';
import CarsStore from './CarsStore';
import UserStore from './UserStore';
import SidebarStore from './SidebarStore';
import ClientsStore from './ClientsStore';
import WarehousesStore from './WarehousesStore';
import NotificationsStore from './NotificationsStore';

const {
  AUTO_MANAGEMENT,
  SENDED_TO_WAREHOUSE,
  WAREHOUSE_CHECK,
  WAREHOUSE_CHECK_ENDED,
  COMPLETED_UNSOLDED,
  COMPLETED_UNAVAILABLE,
} = requestProgresses;
class RequestsStore {
  @observable openedRequests = [];
  @observable requests = {};
  @observable requestsParams = {
    hasChanged: false,
    saving: false,
    hasBeenPrintedModal: {
      active: false,
    },
    deepSearch: {
      active: false,
    },
    timeInterval: {
      active: true,
      start: getTimestamp(),
      end: getTimestamp() + 604800,
    },

    progressSearch: {
      active: true,
      value: 'tutti i progressi',
    },

    leaveRequestModal: {
      open: false,
    },
    tableKeys: [
      ' ',
      'CLIENTE',
      'AUTO',
      'VENDITORE',
      'LOC',
      'VERIFICATI',
      'PROGRESSO',
      'DISP.',
      'COMUNICAZIONE',
      'CREATA',
    ],
    searchValue: '',
    searchNotFound: false,
    itemLoaded: 100,
    itemsToLoad: 100,
    requestsLoaded: false,
    requestNotFound: false,
    filter: {
      active: false,
      status: 'attiva',
    },
    orderBy: {
      prop: 'timestamp',
      direction: 'asc',
    },
    clientSideFilters: {
      byAvailability: null,
      byCommunication: null,
      bySeller: null,
      byLocation: null,
    },
    filterByPartId: null,
    reqChannelSelected: false,
    clientLoaded: false,
    currentRequestLoaded: false,
    reqPartForm: {
      open: false,
      currentPartId: null,
    },
    requestNoteModal: false,
    reqOptionForm: {
      open: false,
      currentPartId: null,
      currentOptionId: null,
    },
    communicationForm: {
      open: false,
      errors: [],
    },
    reqChannels: [],
  };

  @observable currentRequest = {
    data: {
      id: '',
      _client: '',
      _seller: '',
      clientMail: '',
      clientName: '',
      reqChannel: '',
      reqShippingType: '',
      progress: null,
      seller: '',
      status: 'attiva',
      whZones: [],
      hasBeenPrinted: false,
      archived: false,
      checked: false,
      reqPartsChecked: 0,
      reqPartsNum: 0,
      optionsAvailable: false,
      carModels: [],
    },
    reqParts: {},
    soldOptions: [],
    communication: {
      state: '',
      recallDate: '',
      recallHour: '',
      recallMinutes: '',
      list: [],
    },
  };

  @observable currentReqPartOption = {
    data: {
      _part: '',
      color: '',
      conditions: '',
      internalCode: '',
      notes: '',
      price: '',
      sold: false,
      position: '',
      checked: 'unchecked',
    },
  };

  @observable currentReqPart = {
    data: {
      _carVersion: '',
      _carModel: '',
      _carMaker: '',
      carMaker: '',
      carModel: '',
      carVersion: '',
      _part: '',
      optionsNum: 0,
      optionsChecked: 0,
      part: '',
      whCode: '',
      productCode: '',
      checked: 'unchecked',
      avalaible: false,
      carSide: '',
      partCondition: '',
      color: '',
      notes: '',
      status: 'attivo',
      timestamp: '',
      uploadedFile: false,
      imgUrl: false,
      imgFile: false,
      partGallery: [],
    },
    options: {},
  };

  @observable requestsErrors = {
    reqPart: [],
    reqPartOption: [],
  };

  @observable currentCommunication = {
    timestamp: null,
    note: '',
    state: '',
    recallDate: '',
    recallHour: '',
    recallMinutes: '',
  };

  @action fetchRequests = (filterChange, originalFilter) => {
    this.requestsParams.requestsLoaded = false;

    if (originalFilter !== this.requestsParams.filter.status && filterChange) {
      const originalRequestsRef = database
        .ref('/requests')
        .orderByChild('data/status')
        .equalTo(originalFilter)
        .limitToLast(this.requestsParams.itemLoaded);
      originalRequestsRef.off('value');
    }

    let requestsRef;

    if (!this.requestsParams.deepSearch.active) {
      requestsRef = database
        .ref('/requests')
        .orderByChild('data/status')
        .equalTo(this.requestsParams.filter.status)
        .limitToLast(this.requestsParams.itemLoaded);
    } else {
      requestsRef = database
        .ref('/requests')
        .orderByChild('data/timestamp')
        .startAt(this.requestsParams.timeInterval.start)
        .endAt(this.requestsParams.timeInterval.end);
    }

    requestsRef.off('value');

    const user = UserStore.user;

    requestsRef.on('value', requestsSnapshot => {
      this.requestsParams.requestsLoaded = true;

      if (!requestsSnapshot.exists()) {
        this.requests = {};

        return;
      }

      if (!user || !user.shouldOnlySeeOwnRequests) {
        this.requests = requestsSnapshot.val();

        return;
      }

      const requests = requestsSnapshot.val();

      for (const key in requests) {
        const request = requests[key];

        if (request.data._seller !== user.uid) {
          delete requests[key];
        }
      }

      this.requests = requests;
    });
  };

  @action fetchReqChannels = () => {
    let reqChannels = this.requestsParams.reqChannels;
    let reqChannelsRef = database.ref('/reqChannels');
    reqChannelsRef.once('value', reqChannelSnapshot => {
      let reqChannelValue = reqChannelSnapshot.val();
      reqChannelValue.forEach(reqChannel => {
        reqChannels.push({
          text: reqChannel.data.channelName,
          value: reqChannel.data.channelName,
          icon: reqChannel.data.icon,
          key: reqChannel.data.channelName,
        });
      });
    });
  };

  @action assignKey = () => {
    let requestsKeys = Object.keys(this.requests);
    let requestsValues = Object.values(this.requests);

    requestsValues.forEach((request, index) => {
      request.data.key = requestsKeys[index];
    });
  };

  @action filterRequests = filterValue => {
    const originalFilter = this.requestsParams.filter.status;
    this.requestsParams.filter.status = filterValue;
    this.fetchRequests(true, originalFilter);
  };

  @action searchRequest = value => {
    this.requestsParams.searchValue = value;
  };

  @action sortRequestsBy = key => {
    const ORDER_BY_MAP = {
      CLIENTE: 'clientName',
      AUTO: 'carModels',
      VENDITORE: 'seller',
      VERIFICATI: 'reqPartsChecked',
      PROGRESSO: 'progress',
      CREATA: 'timestamp',
      COMUNICAZIONE: 'communicationRecall',
    };

    const orderBy = {
      ...this.requestsParams.orderBy,
      prop: ORDER_BY_MAP[key],
    };

    if (this.requestsParams.orderBy.prop === orderBy.prop) {
      orderBy.direction = orderBy.direction === 'asc' ? 'desc' : 'asc';
    }

    this.requestsParams = {
      ...this.requestsParams,
      orderBy,
    };
  };

  @action loadMoreRequests = () => {
    this.requestsParams.itemLoaded += this.requestsParams.itemsToLoad;
    this.fetchRequests();
  };

  @action handleReqChannelSelect = value => {
    this.currentRequest.data.reqChannel = value;
    this.requestsParams.reqChannelSelected = true;
    this.requestsParams.hasChanged = true;
  };

  @action handleReqTypeSelect = value => {
    this.currentRequest.data.reqShippingType = value;
  };

  @action loadCurrentRequest = (requestId, userRole, loadClient, isSale) => {
    let $this = this;
    if (SidebarStore.sidebar.open) {
      SidebarStore.toggleSidebar();
    }
    if (!isSale) {
      SidebarStore.disableSidebar();
    }
    let currentRequestRef = database.ref('/requests/' + requestId);
    currentRequestRef.on('value', currentRequestSnapshot => {
      $this.openedRequests.forEach(id => {
        if (id !== requestId) {
          database.ref('/requests/' + id).off('value');
        }
      });
      $this.openedRequests = [requestId];
      let requestValue = currentRequestSnapshot.val();
      let partObj = {};
      if (requestValue !== undefined && requestValue !== null) {
        this.requestsParams.requestNotFound = false;
        let requestPartsKeys = Object.keys(requestValue.reqParts);
        requestPartsKeys.forEach(parKey => {
          if (
            requestValue.reqParts[parKey].options === undefined ||
            requestValue.reqParts[parKey].options === null
          ) {
            requestValue.reqParts[parKey].options = {};
          }
        });
        $this.currentRequest.data = {
          ...requestValue.data,
          key: requestId,
        };

        requestValue.reqParts.forEach((part, index) => {
          if (typeof part === 'undefined') {
            partObj[index] = null;
          } else {
            partObj[index] = part;
          }
        });

        $this.currentRequest.communication = requestValue.communication;
        if (!$this.currentRequest.communication) {
          $this.currentRequest.communication = {
            state: '',
            recallDate: '',
            recallHour: '',
            recallMinutes: '',
            list: [],
          };
        }
        if (!$this.currentRequest.communication.list) {
          $this.currentRequest.communication.list = [];
        }
        $this.currentRequest.reqParts = partObj;
        $this.currentRequest.soldOptions = requestValue.soldOptions;
        $this.requestsParams.clientLoaded = true;
        $this.requestsParams.reqChannelSelected = true;
        $this.requestsParams.currentRequestLoaded = true;
        $this.requestsParams.currentRequestId = requestId;
        if (
          $this.currentRequest.soldOptions !== undefined &&
          $this.currentRequest.soldOptions !== null
        ) {
          if (typeof $this.currentRequest.soldOptions === 'object') {
            $this.currentRequest.soldOptions = Object.values(
              $this.currentRequest.soldOptions,
            );
          } else {
            $this.currentRequest.soldOptions = $this.currentRequest.soldOptions;
          }
        } else {
          $this.currentRequest.soldOptions = [];
        }

        if (
          userRole === 'warehouse' &&
          $this.currentRequest.data.reqPartsNum >
            $this.currentRequest.data.reqPartsChecked
        ) {
          $this.currentRequest.data.progress = 'verifica magazzino';
          this.checkOptionsChecked(true, requestId);
        }
        if (
          userRole === 'seller' &&
          $this.currentRequest.data.progress === 'controllo terminato'
        ) {
          $this.currentRequest.data.progress = 'contrattazione vendita';
          this.checkOptionsChecked(true, requestId);
        }
        if (loadClient) {
          ClientsStore.loadCurrentClient($this.currentRequest.data._client);
        }
      } else {
        this.requestsParams.requestNotFound = true;
      }
    });

    $this.requestsParams.reqPartForm.open = false;
    $this.requestsParams.reqPartForm.currentPartId = null;
    $this.requestsParams.reqOptionForm.open = false;
    $this.requestsParams.reqOptionForm.currentPartId = null;
    $this.requestsParams.reqOptionForm.currentOptionId = null;
    $this.currentReqPart.data = { ...newReqPart.data };
    $this.currentReqPart.options = { ...newReqPart.options };
    $this.currentReqPartOption.data = { ...newPartOption.data };
  };

  @action togglePartForm = () => {
    if (this.requestsParams.reqPartForm.open) {
      this.requestsParams.reqPartForm.open = false;
    } else {
      this.requestsParams.reqPartForm.open = true;
    }
  };

  @action setRequestClient = (clientId, name, surname) => {
    this.currentRequest.data._client = clientId;
    this.currentRequest.data.clientName = name + ' ' + surname;
    this.requestsParams.hasChanged = true;
  };

  @action resetErrorReqPart = name => {
    this.requestsErrors.reqPart.forEach((error, index) => {
      if (error === name) {
        this.requestsErrors.reqPart.splice(index, 1);
      }
    });
  };

  @action resetErrorReqPartOption = name => {
    this.requestsErrors.reqPartOption.forEach((error, index) => {
      if (error === name) {
        this.requestsErrors.reqPartOption.splice(index, 1);
      }
    });
  };

  @action handleWhZoneSelect = value => {
    this.currentReqPart.data.whCode = value;
    CarPartsStore.fetch();
    this.requestsParams.hasChanged = true;
  };

  @action handleCarPartSelect = selection => {
    let currentPart = null;
    selection.options.forEach(option => {
      if (option.value === selection.value) {
        currentPart = option;
      }
    });

    let whCode = CarPartsStore.items[currentPart.id].data.whCode;
    this.currentReqPart.data.part = currentPart.text;
    this.currentReqPart.data._part = currentPart.id;
    this.currentReqPart.data.whCode = whCode;
    this.requestsParams.hasChanged = true;
  };

  @action resetCurrentRequest = () => {
    database
      .ref('/requests/' + this.requestsParams.currentRequestId)
      .off('value');
    SidebarStore.activeSidebar();
    this.requestsParams.hasChanged = false;
    this.requestsParams.clientLoaded = false;
    this.requestsParams.reqChannelSelected = false;
    this.requestsParams.currentRequestLoaded = false;
    this.requestsParams.currentRequestId = null;
    this.currentRequest.data = { ...newRequest.data };
    this.currentRequest.reqParts = { ...newRequest.reqParts };
    this.currentRequest.communication = {
      state: '',
      recallDate: '',
      recallHour: '',
      recallMinutes: '',
      list: [],
    };
    this.currentRequest.soldOptions = [];
  };

  @action resetHasBeenChanged = () => {
    this.requestsParams.hasChanged = false;
  };

  @action handleCarPartInput = (value, name) => {
    this.currentReqPart.data[name] = value;
    this.requestsParams.hasChanged = true;
  };

  @action handleCarMakerSelect = selection => {
    let currentOption = null;
    let selectionOptions = selection.options;
    let selectionValue = selection.value;
    selectionOptions.forEach(option => {
      if (option.value === selectionValue) {
        currentOption = option;
      }
    });
    this.currentReqPart.data._carMaker = currentOption.id;
    this.currentReqPart.data.carMaker = currentOption.value;
    CarsStore.fetchCarModels(this.currentReqPart.data._carMaker);
    this.requestsParams.hasChanged = true;
  };

  @action handleCarModelSelect = selection => {
    let currentOption = null;
    let selectionOptions = selection.options;
    let selectionValue = selection.value;
    selectionOptions.forEach(option => {
      if (option.value === selectionValue) {
        currentOption = option;
      }
    });
    this.currentReqPart.data._carModel = currentOption.id;
    this.currentReqPart.data.carModel = currentOption.value;
    CarsStore.fetchCarVersions(this.currentReqPart.data._carModel);
    this.requestsParams.hasChanged = true;
  };

  @action handleCarVersionSelect = selection => {
    let currentOption = null;
    let selectionOptions = selection.options;
    let selectionValue = selection.value;
    selectionOptions.forEach(option => {
      if (option.value === selectionValue) {
        currentOption = option;
      }
    });
    this.currentReqPart.data._carVersion = currentOption.id;
    this.currentReqPart.data.carVersion = currentOption.value;
    this.requestsParams.hasChanged = true;
  };

  @action handlePartFormSubmit = (type, requestId) => {
    let $this = this;
    let errors = validateForm($this.currentReqPart.data, formModels.reqPart);
    if (errors.length > 0) {
      $this.requestsErrors.reqPart = errors;
    } else {
      $this.currentReqPart.data.timestamp = getTimestamp();

      if (
        !$this.currentRequest.data.carModels.includes(
          $this.currentReqPart.data.carMaker +
            ' ' +
            $this.currentReqPart.data.carVersion,
        )
      ) {
        $this.currentRequest.data.carModels.push(
          $this.currentReqPart.data.carMaker +
            ' ' +
            $this.currentReqPart.data.carVersion,
        );
      }

      if (
        !$this.currentRequest.data.whZones.includes(
          $this.currentReqPart.data.whCode,
        )
      ) {
        $this.currentRequest.data.whZones.push(
          $this.currentReqPart.data.whCode,
        );
      }

      let reqPartsNum = Object.keys($this.currentRequest.reqParts);
      let newReqPartId;

      if (type === 'new') {
        newReqPartId =
          reqPartsNum.length === 0
            ? 0
            : Number(reqPartsNum[reqPartsNum.length - 1]) + 1;
        $this.currentRequest.data.reqPartsNum =
          $this.currentRequest.data.reqPartsNum + 1;
      } else {
        newReqPartId = $this.requestsParams.reqPartForm.currentPartId;
      }

      $this.currentRequest.reqParts[newReqPartId] = {
        data: { ...$this.currentReqPart.data },
        options: { ...$this.currentReqPart.options },
      };

      if (requestId !== null && requestId !== undefined) {
        $this.checkOptionsChecked(true, requestId);
      } else {
        $this.checkOptionsChecked();
      }

      $this.resetCurrentReqPart();
      $this.togglePartForm();
    }
    this.requestsParams.hasChanged = true;
  };

  @action loadCurrentPart = partId => {
    let currentPart = this.currentRequest.reqParts[partId];
    this.currentReqPart.data = { ...currentPart.data };
    this.currentReqPart.options = { ...currentPart.options };
    this.requestsParams.reqPartForm.currentPartId = partId;
    CarsStore.fetchCarModels(this.currentReqPart.data._carMaker);
    CarsStore.fetchCarVersions(this.currentReqPart.data._carModel);
  };

  @action resetCurrentReqPart = () => {
    this.requestsParams.reqPartForm.currentPartId = null;
    CarsStore.carsModels = [];
    CarsStore.carsVersions = [];
    this.currentReqPart = {
      data: { ...newReqPart.data },
      options: { ...newReqPart.options },
    };
  };

  @action toggleOptionForm = (partId, isNewOption) => {
    this.requestsParams.reqOptionForm.open =
      !this.requestsParams.reqOptionForm.open;
    if (isNewOption) {
      this.requestsParams.reqOptionForm.currentPartId = partId;
    }
  };

  @action handleReqPartRemove = (partId, requestId) => {
    this.currentRequest.data.reqPartsNum =
      Number(this.currentRequest.data.reqPartsNum) - 1;
    if (
      this.currentRequest.data.reqPartsNum <
      this.currentRequest.data.reqPartsChecked
    ) {
      this.currentRequest.data.reqPartsChecked =
        Number(this.currentRequest.data.reqPartsChecked) - 1;
    }
    delete this.currentRequest.reqParts[partId];
    if (requestId !== null && requestId !== undefined) {
      this.checkOptionsChecked(true, requestId);
    }
    this.requestsParams.hasChanged = true;
  };

  @action handlePartOptionInput = (value, name) => {
    this.currentReqPartOption.data[name] = value;
    this.requestsParams.hasChanged = true;
  };

  @action handleReqOptionRemove = (partId, optionId, requestId) => {
    this.currentRequest.reqParts[partId].data.optionsNum =
      Number(this.currentRequest.reqParts[partId].data.optionsNum) - 1;
    if (
      this.currentRequest.reqParts[partId].data.optionsNum <
      this.currentRequest.reqParts[partId].data.optionsChecked
    ) {
      this.currentRequest.reqParts[partId].data.optionsChecked =
        Number(this.currentRequest.reqParts[partId].data.optionsChecked) - 1;
    }
    delete this.currentRequest.reqParts[partId].options[optionId];
    if (requestId !== null && requestId !== undefined) {
      this.checkOptionsChecked(true, requestId);
    }
    this.requestsParams.hasChanged = true;
  };

  @action loadCurrentOption = (partId, optionId) => {
    this.requestsParams.reqOptionForm.currentOptionId = optionId;
    this.requestsParams.reqOptionForm.currentPartId = partId;
    let currentOption = this.currentRequest.reqParts[partId].options[optionId];
    this.currentReqPartOption.data = currentOption.data;
  };

  @action checkOption = (available, requestId, partId, optionKey) => {
    if (
      this.currentRequest.reqParts[partId].options[optionKey].data.checked ===
      'unchecked'
    ) {
      this.currentRequest.reqParts[partId].data.optionsChecked += 1;
    }
    this.currentRequest.reqParts[partId].options[optionKey].data.checked =
      available;
    this.requestsParams.hasChanged = true;
    this.checkOptionsChecked(true, requestId);
  };

  @action soldOption = (sold, requestId, partId, optionKey) => {
    let $this = this;
    let requestRef = database.ref('/requests/' + requestId);
    $this.currentRequest.reqParts[partId].options[optionKey].data.sold = sold;
    if (sold) {
      $this.currentRequest.soldOptions.push({
        _partId: partId,
        _optionKey: optionKey,
      });
    } else {
      $this.currentRequest.soldOptions.forEach((soldOption, index) => {
        if (
          soldOption._partId === partId &&
          soldOption._optionKey === optionKey
        ) {
          $this.currentRequest.soldOptions.splice(index, 1);
        }
      });
    }
    requestRef.set($this.currentRequest);
  };

  @action handlePartOptionFormSubmit = requestId => {
    let $this = this;
    let errors = validateForm(
      this.currentReqPartOption.data,
      formModels.reqPartOption,
    );

    let partId = $this.requestsParams.reqOptionForm.currentPartId;

    let reqOptionsKeys = Object.keys(
      $this.currentRequest.reqParts[partId]?.options || {},
    );

    let optionId =
      reqOptionsKeys.length === 0
        ? 0
        : Number(reqOptionsKeys[reqOptionsKeys.length - 1]) + 1;
    if ($this.requestsParams.reqOptionForm.currentOptionId !== null) {
      optionId = $this.requestsParams.reqOptionForm.currentOptionId;
    }
    if (errors.length > 0) {
      $this.requestsErrors.reqPartOption = errors;
    } else {
      $this.currentReqPartOption.data.timestamp = getTimestamp();
      if ($this.requestsParams.reqOptionForm.currentOptionId === null) {
        $this.currentRequest.reqParts[partId].data.optionsNum =
          Number($this.currentRequest.reqParts[partId].data.optionsNum) + 1;
      }
      if (
        $this.currentRequest.reqParts[partId].data.optionsNum ===
          $this.currentRequest.reqParts[partId].data.optionsChecked &&
        $this.currentRequest.data.reqPartsNum >
          $this.currentRequest.data.reqPartsChecked
      ) {
        $this.currentRequest.data.reqPartsChecked =
          $this.currentRequest.data.reqPartsChecked + 1;
      }

      $this.currentRequest.reqParts[partId].options[optionId] = {
        data: { ...$this.currentReqPartOption.data },
      };

      $this.currentRequest.reqParts[partId].options[optionId].data.addedBy =
        UserStore.userFullName;

      $this.currentRequest.reqParts[partId].options[optionId].data.addedByRole =
        UserStore.roleIt;

      $this.resetCurrentOption();
      if (requestId !== null && requestId !== undefined) {
        $this.checkOptionsChecked(true, requestId);
      } else {
        $this.checkOptionsChecked();
      }
    }
    this.requestsParams.hasChanged = true;
  };

  @action resetCurrentOption() {
    this.currentReqPartOption.data = { ...newPartOption.data };
    this.requestsParams.reqOptionForm.currentPartId = null;
    this.requestsParams.reqOptionForm.currentOptionId = null;
  }

  @action checkOptionsChecked(whCheck, requestId) {
    let $this = this;
    let requestParts = $this.currentRequest.reqParts;
    let requestsPartsKeys = Object.keys(requestParts);
    if (requestsPartsKeys.length > 0) {
      $this.currentRequest.data.reqPartsChecked = 0;
      requestsPartsKeys.forEach(partKey => {
        let part = requestParts[partKey];
        if (part.data.optionsNum === part.data.optionsChecked) {
          $this.currentRequest.data.reqPartsChecked += 1;
        }
      });
    }
    if (whCheck === true) {
      let requestRef = database.ref('/requests/' + requestId);
      requestRef.set($this.currentRequest).then(() => {
        $this.savePartGalleries(requestId);
      });
    }
  }

  @action savePartGalleries = (requestId, cb) => {
    const $this = this;

    let galleries = [];
    Object.keys($this.currentRequest.reqParts).forEach(key => {
      galleries.push($this.currentRequest.reqParts[key].data.partGallery);
      delete $this.currentRequest.reqParts[key].data.partGallery;
    });

    Object.keys($this.currentRequest.reqParts).forEach(key => {
      let currentGallery = galleries[key];
      if (typeof currentGallery !== 'undefined') {
        currentGallery.forEach((image, index) => {
          if (image.indexOf('base64') >= 0) {
            let imageRef = storage.ref(
              `/images/requests/${requestId}/reqParts/${key}/${index}.png`,
            );
            if (image.indexOf('base64') >= 0) {
              imageRef.putString(image, 'data_url').then(function () {
                imageRef.getDownloadURL().then(function (downloadURL) {
                  let partImageRef = database.ref(
                    `/requests/${requestId}/reqParts/${key}/data/partGallery/${index}`,
                  );
                  partImageRef.set(downloadURL);
                });
              });
            }
          } else {
            let partGalleryRef = database.ref(
              `/requests/${requestId}/reqParts/${key}/data/partGallery/`,
            );
            partGalleryRef.set(galleries[key]);
            $this.currentRequest.reqParts[key].data.partGallery =
              galleries[key];
          }
        });
      }
    });

    this.requestsParams.hasChanged = true;
  };

  @action formatPartOptionPrice = () => {
    if (
      this.currentReqPartOption.data.price !== undefined &&
      this.currentReqPartOption.data.price !== null &&
      this.currentReqPartOption.data.price !== ''
    ) {
      this.currentReqPartOption.data.price = Number(
        this.currentReqPartOption.data.price,
      ).toFixed(2);
    }
  };

  @action quickAutoSelect = selection => {
    let reqParts = Object.values(this.currentRequest.reqParts);
    reqParts.forEach(reqPart => {
      let selectedVersion = selection.value;
      let reqPartVersion =
        reqPart.data.carMaker + ' ' + reqPart.data.carVersion;
      if (selectedVersion === reqPartVersion) {
        this.currentReqPart.data._carMaker = reqPart.data._carMaker;
        this.currentReqPart.data._carModel = reqPart.data._carModel;
        this.currentReqPart.data._carVersion = reqPart.data._carVersion;
        this.currentReqPart.data.carMaker = reqPart.data.carMaker;
        this.currentReqPart.data.carModel = reqPart.data.carModel;
        this.currentReqPart.data.carVersion = reqPart.data.carVersion;
      }
    });
    CarsStore.fetchCarModels(this.currentReqPart.data._carMaker);
    CarsStore.fetchCarVersions(this.currentReqPart.data._carModel);
  };

  @action searchRequestDeep = () => {
    this.requestsParams.timeInterval.active = true;
    this.fetchRequests();
  };

  @action openDeepSearch = () => {
    this.requestsParams.deepSearch.active = true;
    this.fetchRequests();
  };

  @action closeDeepSearch = () => {
    this.requestsParams.deepSearch.active = false;
    this.requestsParams.progressSearch.value = 'tutti i progressi';
    this.fetchRequests();
  };

  @action timeIntervalInput = (date, name) => {
    this.requestsParams.timeInterval[name] = -getTimestampFromDate(date);
    this.fetchRequests();
  };

  @action progressSearchInput = result => {
    this.requestsParams.progressSearch.value = result.children;
  };

  @action handleCancelRequest() {
    this.resetCurrentRequest();
  }

  @action addImgToPart = e => {
    const $this = this;
    const file = e.target.files[0];
    let reader = new FileReader();
    let partGallery =
      typeof $this.currentReqPart.data.partGallery === 'undefined'
        ? []
        : $this.currentReqPart.data.partGallery;

    if (!file) {
      return;
    }

    new Compressor(file, {
      maxWidth: 600,
      quality: 0.3,
      success(result) {
        reader.readAsDataURL(result);
        reader.onloadend = function () {
          partGallery.push(reader.result);
          $this.currentReqPart.data.partGallery = partGallery;
        };
      },
      error(err) {
        console.log(err.message);
      },
    });

    this.requestsParams.hasChanged = true;
  };

  @action initializeGallery = () => {
    this.currentReqPart.data.partGallery = [];
  };

  @action removeImgFromPart = index => {
    let partGallery = [...this.currentReqPart.data.partGallery];
    partGallery.splice(index, 1);
    if (partGallery.length === 0) {
      this.initializeGallery();
    } else {
      this.currentReqPart.data.partGallery = partGallery;
    }
    this.requestsParams.hasChanged = true;
  };

  @action handleAutoManagementSaveRequest = cb => {
    this.requestsParams.saving = true;
    this.currentRequest.data._seller = UserStore.user.uid;
    this.currentRequest.data.seller = UserStore.userFullName;
    this.currentRequest.data.fromPlace = UserStore.user.location;
    // this.currentRequest.data.toPlace = WarehousesStore.getFirstWarehouse(
    //   UserStore.user.location
    // );
    // Modifico la gestione del toPlace che in questo caso è la stessa del from in quanto
    // La richiesta è AUTOGESTITA
    this.currentRequest.data.toPlace = UserStore.user.location;
    // Setto un nuovo attributo `autoManagement` che indica che la richiesta è auto gestita
    this.currentRequest.data.autoManagement = true;
    this.currentRequest.data.timestamp = getTimestamp();
    this.currentRequest.data.progress = AUTO_MANAGEMENT;
    const requestsRef = database.ref('/requests');
    requestsRef.push(this.currentRequest).then(requestSnap => {
      const clientRequestsRef = database.ref(
        `/clients/${this.currentRequest.data._client}/requests/${requestSnap.key}`,
      );
      NotificationsStore.createNotification(
        requestSnap.key,
        this.currentRequest.data.whZones,
        'newRequest',
      );
      clientRequestsRef
        .set({
          timestamp: getTimestamp(),
          reqPartsNum: this.currentRequest.data.reqPartsNum,
        })
        .then(() => {
          ClientsStore.resetCurrentClient();
          this.savePartGalleries(requestSnap.key);
          this.requestsParams.saving = false;
          cb();
        });
    });
  };

  @action handleAutoManagementCheckRequest = () => {
    this.currentRequest.data.lastModified = getTimestamp();
    this.currentRequest.data.progress = WAREHOUSE_CHECK_ENDED;
  };

  @action handleSendToWareHouseRequest = () => {
    this.currentRequest.data.fromPlace = UserStore.user.location;
    this.currentRequest.data.toPlace = WarehousesStore.getFirstWarehouse(
      UserStore.user.location,
    );
    this.currentRequest.data.timestamp = getTimestamp();
    this.currentRequest.data.autoManagement = false;
    this.currentRequest.data.progress = SENDED_TO_WAREHOUSE;
  };

  @action handleNewRequest = cb => {
    this.currentRequest.data._seller = UserStore.user.uid;
    this.currentRequest.data.seller = UserStore.userFullName;
    this.currentRequest.data.fromPlace = UserStore.user.location;
    this.currentRequest.data.toPlace = WarehousesStore.getFirstWarehouse(
      UserStore.user.location,
    );
    this.currentRequest.data.timestamp = getTimestamp();
    this.currentRequest.data.progress = SENDED_TO_WAREHOUSE;
    let requestsRef = database.ref('/requests');
    requestsRef.push(this.currentRequest).then(requestSnap => {
      let clientRequestsRef = database.ref(
        `/clients/${this.currentRequest.data._client}/requests/${requestSnap.key}`,
      );
      NotificationsStore.createNotification(
        requestSnap.key,
        this.currentRequest.data.whZones,
        'newRequest',
      );
      clientRequestsRef
        .set({
          timestamp: getTimestamp(),
          reqPartsNum: this.currentRequest.data.reqPartsNum,
        })
        .then(() => {
          ClientsStore.resetCurrentClient();
          this.savePartGalleries(requestSnap.key);
          this.requestsParams.saving = false;
          cb();
        });
    });
  };

  @action handleRequestSave = (type, requestId, cb) => {
    let $this = this;
    $this.requestsParams.saving = true;
    if (UserStore.user.isExternal) {
      this.currentRequest.data.editedByExternal = true;
    }

    if (type === requestStatus.NEW) {
      return this.handleNewRequest(cb);
    }
    if (type === requestStatus.AUTO_MANAGEMENT) {
      return this.handleAutoManagementSaveRequest(cb);
    }

    if (type === requestStatus.UPDATE) {
      NotificationsStore.createNotification(
        requestId,
        $this.currentRequest.data.whZones,
        'updateRequest',
      );
      $this.currentRequest.data.lastModified = getTimestamp();
      $this.currentRequest.data.progress = SENDED_TO_WAREHOUSE;
    }
    if (type === requestStatus.CHECK_SAVE) {
      $this.currentRequest.data.lastModified = getTimestamp();
      $this.currentRequest.data.progress = WAREHOUSE_CHECK;
    }
    if (type === requestStatus.CHECKED) {
      let sellerLocation = this.currentRequest.data.fromPlace;
      let currentWarehouse = this.currentRequest.data.toPlace;
      let nextWarehouse = WarehousesStore.getNextWarehouse(
        sellerLocation,
        currentWarehouse,
      );
      if (this.hasUnavailableParts && nextWarehouse) {
        $this.currentRequest.data.lastModified = getTimestamp();
        $this.currentRequest.data.toPlace = nextWarehouse;
        $this.currentRequest.data.progress = SENDED_TO_WAREHOUSE;
      } else {
        NotificationsStore.createNotification(
          requestId,
          null,
          'requestChecked',
        );
        $this.currentRequest.data.lastModified = getTimestamp();
        $this.currentRequest.data.progress = WAREHOUSE_CHECK_ENDED;
      }
    }
    if (type === requestStatus.AUTO_CHECKED) {
      this.handleAutoManagementCheckRequest();
    }
    if (type === requestStatus.SEND_TO_WAREHOUSE) {
      this.handleSendToWareHouseRequest();
    }
    if (type === requestStatus.UNSOLDED || type === requestStatus.UNAVAILABLE) {
      $this.currentRequest.data.lastModified = getTimestamp();
      $this.currentRequest.data.progress =
        type === requestStatus.UNSOLDED
          ? COMPLETED_UNSOLDED
          : COMPLETED_UNAVAILABLE;
      $this.currentRequest.data.status = requestStatus.ARCHIVED;
    }
    const requestsRef = database.ref('/requests/' + requestId);

    console.log({ currentRequest: $this.currentRequest });

    requestsRef
      .set($this.currentRequest)
      .then(() => {
        ClientsStore.resetCurrentClient();
        $this.savePartGalleries(requestId);
        $this.requestsParams.saving = false;
        cb();
      })
      .catch(err => {
        console.error('Error while saving request', err);
      });
  };

  /**
   * Archivia le richieste più vecchie della data specificata come parametro
   * @param {Date} date La data
   */
  @action archiveRequests = date => {
    const requestsRef = database.ref('/requests/');
    return new Promise((resolve, reject) => {
      requestsRef.on('value', async snap => {
        const requests = snap.val();
        let keys = Object.keys(requests);
        keys = keys.filter(key => {
          const request = requests[key];
          const timestamp = new Date(request.data.timestamp * 1000 * -1);
          return (
            request.data.status !== 'archiviata' &&
            timestamp.getTime() <= date.getTime()
          );
        });

        let archived = [];

        keys.forEach(async key => {
          const request = requests[key];
          await database.ref(`/requests/${key}/data/`).set({
            ...request.data,
            status: 'archiviata',
          });
          archived.push(key);
        });

        resolve(archived);
      });
    });
  };

  @action toggleRequestNoteModal = () => {
    if (this.requestsParams.requestNoteModal) {
      if (
        typeof this.requestsParams.currentRequestId !== 'undefined' &&
        this.requestsParams.currentRequestId !== '' &&
        this.requestsParams.currentRequestId !== 'null' &&
        this.currentRequest &&
        this.currentRequest.data
      ) {
        let noteRef = database.ref(
          '/requests/' + this.requestsParams.currentRequestId + '/data/note/',
        );
        noteRef.set(this.currentRequest.data.note);
      }
    }
    this.requestsParams.requestNoteModal =
      !this.requestsParams.requestNoteModal;
  };

  @action editRequestNote = value => {
    this.currentRequest.data.note = value;
    this.requestsParams.hasChanged = true;
  };

  @action needDemolitionCheck = reqParts => {
    let arr = [];
    if (reqParts !== undefined) {
      Object.values(reqParts).forEach(reqPart => {
        if (reqPart !== undefined && reqPart !== null) {
          if (
            reqPart.options === undefined ||
            Object.values(reqPart.options) === 0
          ) {
            arr.push(false);
          } else {
            arr.push(true);
          }
        }
      });
    }
    let needDemolitionCheck = arr.includes(false) ? false : true;
    return needDemolitionCheck;
  };

  @action toggleLeaveRequestModal = () => {
    this.requestsParams.leaveRequestModal.open =
      !this.requestsParams.leaveRequestModal.open;
  };

  @action firstRequestPart = reqParts => {
    let cleanedReqParts = [];
    if (reqParts !== null && reqParts !== undefined) {
      Object.values(reqParts).forEach(reqPart => {
        if (reqPart !== null && reqPart !== undefined) {
          cleanedReqParts.push(reqPart);
        }
      });
    } else {
      cleanedReqParts = [{ data: null }];
    }
    return cleanedReqParts[0];
  };

  @action hasPhoto = reqParts => {
    let hasPhoto = false;
    if (reqParts !== null && reqParts !== undefined) {
      Object.values(reqParts).forEach(reqPart => {
        if (reqPart !== null && reqPart !== undefined) {
          if (typeof reqPart.data.partGallery !== 'undefined') {
            hasPhoto = true;
          }
        }
      });
    }
    return hasPhoto;
  };

  @action openHasBeenPrintedModal = () => {
    this.requestsParams.hasBeenPrintedModal.active = true;
  };

  @action closeHasBeenPrintedModal = () => {
    this.requestsParams.hasBeenPrintedModal.active = false;
  };

  @action printRequest = () => {
    var mywindow = window.open('', 'Print', 'height=600,width=800');
    mywindow.document.write(
      `<html><head>
        <title>Riepilogo Richiesta</title>
        <style>
        </style>
      </head>`,
    );
    mywindow.document.write('<body style="font-family: Arial, sans-serif">');
    mywindow.document.write(`
      <h2> Riepilogo Richiesta </h2>
      <p style="margin-top:0 !important; margin-bottom: 3px">
        <strong>Data e ora: </strong> 
        ${getDateFromTimestamp(this.currentRequest.data.timestamp, true)},
        <strong>Nome Cliente: </strong> 
        ${this.currentRequest.data.clientName},
        <strong>Numero di telefono: </strong> 
        ${ClientsStore.currentClient.data.mobPhone},
        <strong>Operatore: </strong> 
        ${this.currentRequest.data.seller}
      </p><br>
      <table>${this.reqPartsPrint}</table>
      <div style="height: 80px"></div>
      ${this.reqImagesPrint}
    `);
    mywindow.document.write('</body></html>');

    mywindow.document.close();
    mywindow.focus();
    mywindow.addEventListener('load', () => {
      mywindow.print();
      const requestId = this.requestsParams.currentRequestId;
      const requestHasBeenPrintedRef = database.ref(
        'requests/' + requestId + '/data/hasBeenPrinted',
      );
      requestHasBeenPrintedRef.set(true);
      mywindow.close();
    });
    return true;
  };

  @action toggleCommunicationModal = () => {
    if (!this.requestsParams.communicationForm.open) {
      this.resetCurrentCommunication();
      this.requestsParams.communicationForm.open = true;
    } else {
      this.requestsParams.communicationForm.open = false;
    }
  };

  @action handleCommunicationInput = (e, input) => {
    this.currentCommunication[input.name] = input.value;
    this.requestsParams.hasChanged = true;
  };

  @action addCommunication = () => {
    let $this = this;
    let errors = validateForm(
      this.currentCommunication,
      formModels.communication,
    );
    if (errors.length) {
      this.requestsParams.communicationForm.errors = errors;
      return false;
    } else {
      this.requestsParams.communicationForm.errors = [];
    }
    let currentCommunication = { ...this.currentCommunication };
    let RequestCommunications = this.currentRequest.communication;
    currentCommunication.timestamp = getTimestamp();
    currentCommunication.user = UserStore.user.name;
    RequestCommunications.state = currentCommunication.state;
    RequestCommunications.recallDate = currentCommunication.recallDate;
    RequestCommunications.recallHour = currentCommunication.recallHour;
    RequestCommunications.recallMinutes = currentCommunication.recallMinutes;
    RequestCommunications.list.push(currentCommunication);
    if (
      typeof this.requestsParams.currentRequestId !== 'undefined' &&
      this.requestsParams.currentRequestId !== '' &&
      this.requestsParams.currentRequestId !== 'null'
    ) {
      let currentUnixDate =
        RequestCommunications.recallDate +
        ' ' +
        RequestCommunications.recallHour +
        ':' +
        RequestCommunications.recallMinutes +
        ':00';
      let recallMoment = getTimestampFromDate(currentUnixDate);
      let requestDataCommunicationRef = database.ref(
        '/requests/' +
          $this.requestsParams.currentRequestId +
          '/data/communicationRecall',
      );
      requestDataCommunicationRef.set(recallMoment * -1).then(function () {
        let communicationRef = database.ref(
          '/requests/' +
            $this.requestsParams.currentRequestId +
            '/communication/',
        );
        communicationRef.set(RequestCommunications).then(() => {
          $this.resetCurrentCommunication();
          $this.toggleCommunicationModal();
        });
      });
    } else {
      this.resetCurrentCommunication();
      this.toggleCommunicationModal();
    }
    this.requestsParams.hasChanged = true;
  };

  @action resetCurrentCommunication = () => {
    this.currentCommunication = {
      timestamp: null,
      note: '',
      state: '',
      recallDate: '',
      recallHour: '',
      recallMinutes: '',
    };
  };

  @action hasBeenEditedByExternal = id => {
    const requests = this.requests[id].data;
    return requests.editedByExternal;
  };

  @action hasOptionsAvailableAction = id => {
    let hasOptions = false;
    let reqParts = this.requests[id].reqParts;
    let reqPartsArr = Object.values(reqParts);
    if (reqPartsArr.length) {
      reqPartsArr.forEach(reqPart => {
        if (typeof reqPart !== 'undefined') {
          let options = reqPart.options;
          if (
            typeof options !== 'undefined' &&
            Object.values(options).length !== 0
          ) {
            Object.values(options).forEach(option => {
              if (option && option.data && option.data.checked === true) {
                hasOptions = true;
              }
            });
          }
        }
      });
    }
    return hasOptions;
  };

  @action setClientSideFilter = filter => {
    this.requestsParams.clientSideFilters = {
      ...this.requestsParams.clientSideFilters,
      ...filter,
    };
  };

  @action clearClientSideFilters = () => {
    this.requestsParams.clientSideFilters = {
      byAvailability: null,
      byCommunication: false,
      bySeller: null,
      byLocation: null,
    };
  };

  @action setFilterByPartId = partId => {
    this.requestsParams.filterByPartId = partId;
  };

  @action clearFilterByPartId = () => {
    this.requestsParams.filterByPartId = null;
  };

  @computed get reqPartsPrint() {
    let partList = '';
    if (
      typeof this.requestsParams.currentRequestId !== 'undefined' &&
      this.requestsParams.currentRequestId !== '' &&
      this.requestsParams.currentRequestId !== 'null'
    ) {
      Object.values(this.currentRequest.reqParts).forEach((part, index) => {
        const optionList = Array.isArray(part.options)
          ? part.options
              .filter(option => option.data && option.data.checked)
              .map(option => {
                const { data } = option || {};
                const { color, conditions, price, notes } = data || {};
                return `
                  <li style="margin: 0">
                    <p style="margin-top:0 !important; margin-bottom: 0px">
                      <strong>Colore:</strong>  ${color || '-'},
                      <strong>Condizioni:</strong>  ${conditions || '-'},
                      <strong>Prezzo:</strong> ${price || '-'}
                      <strong>Note:</strong> ${notes || '-'}
                  </li>
                `;
              })
          : [];
        partList += `
          <tr style="font-size: 120%;">
            <td style="vertical-align: top">
              <h4 style="margin-top:0 !important; margin-bottom: 3px">
                ${index + 1}.
              </h4>
            </td>
            <td>
              <h4 style="margin-top:0 !important; margin-bottom: 3px">
                ${part.data.part} - ${part.data.carMaker} ${part.data.carModel}
              </h4>
              <p style="margin-top:0 !important; margin-bottom: 3px">
                ${part.data.carVersion}
              </p>
              <p style="margin-top:0 !important; margin-bottom: 3px"><strong>Codice Prodotto:</strong> 
                ${part.data.productCode !== '' ? part.data.productCode : '-'}
              </p>
              <p style="margin-top:0 !important; margin-bottom: 3px"><strong>Lato Auto:</strong> 
                ${part.data.carSide !== '' ? part.data.carSide : '-'}
              </p>
              <p style="margin-top:0 !important; margin-bottom: 0px"><strong>Colore:</strong> 
                ${part.data.color !== '' ? part.data.color : '-'}
              </p>
              <p style="margin-top:0 !important; margin-bottom: 0px"><strong>Condizione del prodotto:</strong> 
                ${
                  part.data.partCondition !== ''
                    ? productConditionsMapped[part.data.partCondition]
                    : '-'
                }
              </p>
              <p style="margin-top:0 !important; margin-bottom: 0px"><strong>Note:</strong> 
                ${part.data.notes !== '' ? part.data.notes : '-'}
              </p>
              <ol>
                ${optionList.join('')}
              </ol>
            </td>
          </tr>
        `;
      });
    } else {
      partList = null;
    }
    return partList;
  }

  @computed get reqImagesPrint() {
    let imagesPrint = '';
    if (
      typeof this.requestsParams.currentRequestId !== 'undefined' &&
      this.requestsParams.currentRequestId !== '' &&
      this.requestsParams.currentRequestId !== 'null'
    ) {
      Object.values(this.currentRequest.reqParts).forEach((part, index) => {
        if (typeof part.data.partGallery !== 'undefined') {
          part.data.partGallery.forEach(image => {
            imagesPrint += `
              <div style="page-break-inside: avoid">
                  <p>
                      <strong>${part.data.part} - ${part.data.carMaker} ${part.data.carModel}:</strong>
                  </p>
                  <img class="print-image" src="${image}" style="width: 80%"/>
              </div>
            `;
          });
        }
      });
    }
    return imagesPrint;
  }

  @computed get carModelsOptions() {
    let carModelsOption = [];
    if (
      typeof this.requestsParams.currentRequestId !== 'undefined' &&
      this.requestsParams.currentRequestId !== '' &&
      this.requestsParams.currentRequestId !== 'null'
    ) {
      this.currentRequest.data.carModels.forEach(carModel => {
        carModelsOption.push({
          text: carModel,
          value: carModel,
        });
      });
      return carModelsOption;
    }
  }

  @computed get hasUnavailableParts() {
    let hasUnavailableParts = false;
    if (
      typeof this.requestsParams.currentRequestId !== 'undefined' &&
      this.requestsParams.currentRequestId !== '' &&
      this.requestsParams.currentRequestId !== 'null'
    ) {
      let reqParts = this.currentRequest.reqParts;
      let reqPartsArr = Object.values(reqParts);
      reqPartsArr.forEach(reqPart => {
        let options = typeof reqPart !== 'undefined' ? reqPart.options : [];
        if (
          typeof options === 'undefined' ||
          options === null ||
          Object.values(options).length === 0
        ) {
          hasUnavailableParts = true;
        } else {
          Object.values(options).forEach(option => {
            if (
              option.data.checked === 'unchecked' ||
              option.data.checked === false
            ) {
              hasUnavailableParts = true;
            }
          });
        }
      });
    }
    return hasUnavailableParts;
  }

  @computed get hasOptionsAvailable() {
    let hasOptions = false;
    if (
      typeof this.requestsParams.currentRequestId !== 'undefined' &&
      this.requestsParams.currentRequestId !== '' &&
      this.requestsParams.currentRequestId !== 'null'
    ) {
      let reqParts = this.currentRequest.reqParts;
      let reqPartsArr = Object.values(reqParts);
      reqPartsArr.forEach(reqPart => {
        let options = reqPart.options;
        if (
          typeof options !== 'undefined' ||
          Object.values(options).length !== 0
        ) {
          Object.values(options).forEach(option => {
            if (option.data.checked === true) {
              hasOptions = true;
            }
          });
        }
      });
    }
    return hasOptions;
  }

  @computed get isActive() {
    return this.currentRequest.data.status === 'attiva';
  }

  @computed get isChecked() {
    return (
      this.currentRequest.data.reqPartsNum ===
      this.currentRequest.data.reqPartsChecked
    );
  }

  @computed get progress() {
    return this.currentRequest.data.progress;
  }

  @computed get hasReqParts() {
    return this.currentRequest.data.reqPartsNum > 0;
  }

  @computed get isResearchActive() {
    return this.requestsParams.searchValue !== '';
  }

  @computed get requestsAsArray() {
    const requestsValues = this.clientSideFilteredRequests;
    const searchValue = this.requestsParams.searchValue;
    const progressFilter = this.requestsParams.progressSearch.value;

    return _.orderBy(
      requestsValues,
      ['data.' + this.requestsParams.orderBy.prop],
      [this.requestsParams.orderBy.direction],
    ).filter(request => {
      if (searchValue !== '') {
        const carName = `${
          this.firstRequestPart(request.reqParts).data.carMaker
        } ${this.firstRequestPart(request.reqParts).data.carModel}`;

        if (
          `${request.data.clientName} ${carName} ${request.data._client}`
            .toLowerCase()
            .indexOf(searchValue.toLowerCase()) === -1
        ) {
          return false;
        }
      }

      if (progressFilter !== 'tutti i progressi') {
        if (request.data.progress !== progressFilter) {
          return false;
        }
      }

      return request.data.status === this.requestsParams.filter.status;
    });
  }

  @computed get nextRequestId() {
    if (!this.currentRequest) {
      console.log('currentRequest not found');
      return null
    }

    const requests = this.requestsAsArray;

    const currentRequestIndex = requests.findIndex(
      ({ data }) => data.key && data.key === this.currentRequest.data.key,
    );

    if (currentRequestIndex === -1) {
      console.log('currentRequestIndex not found');
      return null;
    }

    const nextRequestEntry = requests[currentRequestIndex + 1];

    if (!nextRequestEntry) {
      console.log('nextRequestEntry not found');

      return null
    }

    const nextRequestId = nextRequestEntry.data.key;

    if (!nextRequestId) {
      console.log('nextRequestId not found');
      return null
    }
    
    return nextRequestId
  }

  @computed get previousRequestId() {
    if (!this.currentRequest) return null;

    const requests = this.requestsAsArray;

    const currentRequestIndex = requests.findIndex(
      ({ data }) => data.key && data.key === this.currentRequest.data.key,
    );

    if (currentRequestIndex === -1) return null;

    const prevRequestEntry = requests[currentRequestIndex - 1];

    if (!prevRequestEntry) return null;

    return prevRequestEntry.data.key;
  }

  @computed get sellerList() {
    const sellers = Object.values(this.requests).reduce((acc, request) => {
      return {
        ...acc,
        [request.data._seller]: request.data.seller,
      };
    }, {});

    return Object.entries(sellers).map(([key, value]) => ({
      key: key,
      value: key,
      text: value,
    }));
  }

  @computed get locationList() {
    const locations = Object.values(this.requests).reduce((acc, request) => {
      if (!request.data.fromPlace) return acc;

      return {
        ...acc,
        [request.data.fromPlace.substring(0, 3).toUpperCase()]:
          request.data.fromPlace,
      };
    }, {});

    return Object.entries(locations).map(([key, value]) => ({
      key: key,
      value: key,
      text: value,
    }));
  }

  @computed get areClientSideFiltersActive() {
    return Object.values(this.requestsParams.clientSideFilters).some(
      val => val,
    );
  }

  @computed get clientSideFilteredRequests() {
    const filters = this.requestsParams.clientSideFilters;

    return Object.fromEntries(
      Object.entries(this.requests).filter(([key, request]) => {
        if (filters.byAvailability) {
          if (filters.byAvailability === 'available') {
            if (!this.hasOptionsAvailableAction(key)) return false;
          } else {
            if (this.hasOptionsAvailableAction(key)) return false;
          }
        }

        if (filters.byCommunication !== null) {
          if (filters.byCommunication === 'with') {
            if (!request.communication || !request.communication.state) {
              return false;
            }
          } else if (filters.byCommunication === 'without') {
            if (request.communication && request.communication.state) {
              return false;
            }
          }
        }

        if (filters.bySeller) {
          if (request.data._seller !== filters.bySeller) return false;
        }

        if (filters.byLocation) {
          if (!request.data.fromPlace) return false;

          if (
            request.data.fromPlace.substring(0, 3).toUpperCase() !==
            filters.byLocation
          ) {
            return false;
          }
        }

        if (this.requestsParams.filterByPartId !== null) {
          if (!request.reqParts || !Array.isArray(request.reqParts)) {
            return false;
          }


          if (request.reqParts.every(part => part && part.data && part.data._part !== this.requestsParams.filterByPartId)) {
            return false;
          }
        }

        return true;
      }),
    );
  };
}

export default new RequestsStore();
